import type { Channel, CompleteMessage } from "@elaso/shared/types/db";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
} from "react";
import { useStore } from "zustand";
import { useSelfPermissionsOnChannel } from "@/hooks/use-accessible-channels";
import { useActiveAsset } from "@/hooks/use-active-asset";
import { useTypingIndicator } from "@/hooks/use-typing-indicator";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { MessageTour } from "./app-tour";
import { ChannelPeoplePopover } from "./channel-people-popover";
import { ChatDropzone } from "./chat-dropzone";
import { MessageInput } from "./message-input";
import { MessageList } from "./message-list";
import { TypingIndicator } from "./typing-indicator";

type ChannelContextType = {
	channelId: string;
	mainChannelId?: string;
	selectedChannel: Channel;
	filteredMessages: CompleteMessage[];
	childrenChannels: Record<string, string>;
	replyingTo: {
		id: string;
		authorId: string;
		richContent: Record<string, unknown>;
	} | null;
	handleReply: (message: {
		id: string;
		authorId: string;
		richContent: Record<string, unknown>;
	}) => void;
	handleCancelReply: () => void;
	handleSendMessage: (
		richContent: Record<string, unknown>,
		attachments?: Array<{
			fileName: string;
			fileSize: number;
			fileType: string;
			objectKey: string;
			url: string;
		}>,
		replyInThread?: boolean
	) => Promise<void>;
};

const ChannelContext = createContext<ChannelContextType | undefined>(undefined);

export function useChannelContext() {
	const context = useContext(ChannelContext);
	if (!context) {
		throw new Error("useChannelContext must be used within ChannelView.Root");
	}
	return context;
}

function ChannelViewRoot({
	channelId,
	mainChannelId,
	children,
}: {
	channelId: string;
	mainChannelId?: string;
	children: ReactNode;
}) {
	const channels = useStore(coreStore, (state) => state.channels);
	const messages = useStore(coreStore, (state) => state.messages);
	const replyingTo = useStore(coreStore, (state) => state.replyingTo);
	const user = useStore(authStore, (state) => state.user);

	const isColdLoading = useStore(coreStore, (state) => state.isColdLoading);
	const fetchMessageHistory = useStore(
		coreStore,
		(state) => state.fetchMessageHistory
	);
	const sendMessage = useStore(coreStore, (state) => state.sendMessage);
	const setReplyingTo = useStore(coreStore, (state) => state.setReplyingTo);
	const markChannelRead = useStore(coreStore, (state) => state.markChannelRead);

	// ChannelView.Root is the one "channel mounted as a full view" choke point
	// (docs/adr/23-the-live-lesson-delivery-tier.md). Text channels and thread
	// panels both render through it, and the typing indicator only exists inside
	// it. This registers the channel into the declared active set, so the server
	// routes viewer-only events here.
	useActiveAsset("channel", channelId);

	// Defer the on-mount message fetch while the cold-connect chain is running,
	// because its slot 6 owns the visible channel's messages and this must not
	// race it. When the chain finishes and `isColdLoading` goes false this re-runs,
	// and fetchMessageHistory no-ops if slot 6 already loaded the channel.
	useEffect(() => {
		if (channelId && !isColdLoading) {
			fetchMessageHistory({ channelId });
		}
	}, [channelId, isColdLoading, fetchMessageHistory]);

	const selectedChannel = channels.get(channelId);

	const childrenChannels = useMemo(
		() =>
			Object.fromEntries(
				Array.from(messages.values())
					.filter((message) => message.threadChannelId)
					.map((message) => [message.id, message.threadChannelId as string])
			),
		[messages]
	);

	const messagesArray = Array.from(messages.values());

	const filteredMessages = messagesArray
		.filter((msg) => msg.channelId === channelId)
		.sort((a, b) =>
			a.createdAt.toString().localeCompare(b.createdAt.toString())
		);

	const latestConfirmedMessageId = filteredMessages.findLast(
		(msg) => !msg.isPending && !msg.hasFailed
	)?.id;

	useEffect(() => {
		if (latestConfirmedMessageId) {
			markChannelRead(channelId, latestConfirmedMessageId);
		}
	}, [latestConfirmedMessageId, channelId, markChannelRead]);

	const handleSendMessage = async (
		richContent: Record<string, unknown>,
		attachments?: Array<{
			fileName: string;
			fileSize: number;
			fileType: string;
			objectKey: string;
			url: string;
		}>,
		replyInThread?: boolean
	) => {
		if (channelId && user?.uid) {
			await sendMessage({
				channelId,
				authorId: user.uid,
				richContent,
				referencedMessageId: replyingTo?.id,
				attachments,
				replyInThread,
			});
			setReplyingTo(null);
		}
	};

	const handleReply = (message: {
		id: string;
		authorId: string;
		richContent: Record<string, unknown>;
	}) => {
		setReplyingTo(message);
	};

	const handleCancelReply = () => {
		setReplyingTo(null);
	};

	if (!selectedChannel) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Channel not found</p>
			</div>
		);
	}

	const contextValue: ChannelContextType = {
		channelId,
		mainChannelId,
		selectedChannel,
		filteredMessages,
		childrenChannels,
		replyingTo,
		handleReply,
		handleCancelReply,
		handleSendMessage,
	};

	return (
		<ChannelContext.Provider value={contextValue}>
			<ChatDropzone className="flex flex-col">{children}</ChatDropzone>
		</ChannelContext.Provider>
	);
}

function ChannelHeader({
	className,
	children,
	...props
}: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className="flex h-14 shrink-0 items-center justify-between border-b px-4 max-md:px-14"
			{...props}
		>
			{children}
		</div>
	);
}

function ChannelHeaderGroup({
	children,
	className,
	...props
}: {
	children: ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div className={`flex h-auto items-center gap-1 ${className}`} {...props}>
			{children}
		</div>
	);
}

function ChannelHeaderTitle({ children }: { children: ReactNode }) {
	return <ChannelHeaderGroup className="pt-0.5">{children}</ChannelHeaderGroup>;
}

function ChannelMessageList() {
	const { channelId, filteredMessages, handleReply, childrenChannels } =
		useChannelContext();

	return (
		<MessageList
			channelId={channelId}
			messages={filteredMessages}
			onReply={handleReply}
			threadIds={childrenChannels}
		/>
	);
}

function ChannelMessageInput() {
	const {
		channelId,
		selectedChannel,
		handleCancelReply,
		handleSendMessage,
		replyingTo,
	} = useChannelContext();
	const users = useStore(coreStore, (state) => state.users);
	const channels = useStore(coreStore, (state) => state.channels);
	const permissionChannelId = selectedChannel.parentId ?? channelId;
	const permissionLevel = useSelfPermissionsOnChannel(permissionChannelId);
	const { startTyping, stopTyping } = useTypingIndicator(channelId);

	const canCreate =
		permissionLevel === "interact" || permissionLevel === "moderate";

	const mentionUsers = useMemo(
		() =>
			Array.from(users.values()).map((entry) => ({
				id: entry.profile.firebaseUid,
				firstName: entry.profile.givenName || undefined,
				lastName: entry.profile.familyName || undefined,
				preferredName: entry.profile.displayName ?? undefined,
			})),
		[users]
	);

	const roles = useStore(coreStore, (state) => state.roles);
	const mentionRoles = useMemo(() => {
		// The channel's role keys, which is all a mention list needs. `/app` never
		// receives the permission values
		// (docs/adr/24-what-the-channel-list-sends-to-the-client.md).
		const channelRoleKeys = channels.get(channelId)?.roleKeys;
		if (!channelRoleKeys) return [];

		const programPrefix = "workspace_role:";

		const result: Array<{
			id: string;
			roleType: "workspace";
			role: string;
			workspaceId: string | undefined;
		}> = [];
		for (const key of channelRoleKeys) {
			if (!key.startsWith(programPrefix)) continue;
			const rest = key.slice(programPrefix.length);
			const separatorIndex = rest.indexOf("--");
			if (separatorIndex < 0) continue;
			const workspaceId = rest.slice(0, separatorIndex);
			const tail = rest.slice(separatorIndex + 2);

			// channel-kind keys grant per-channel access, so they are not mentionable.
			if (tail.startsWith("channel--")) continue;

			if (tail === "everyone") {
				result.push({
					id: key,
					roleType: "workspace",
					role: "everyone",
					workspaceId,
				});
				continue;
			}

			const role = roles.get(tail);
			if (!role) continue;
			result.push({
				id: role.id,
				roleType: "workspace",
				role: role.name,
				workspaceId,
			});
		}
		return result;
	}, [channels, channelId, roles]);

	if (!canCreate) {
		return (
			<div className="shrink-0 p-4">
				<div className="flex items-center justify-center rounded-lg border bg-muted/50 px-4 py-3">
					<p className="text-muted-foreground text-sm">
						You don't have permission to send messages in this channel.
					</p>
				</div>
			</div>
		);
	}

	const handleSendWithStopTyping = async (
		richContent: Record<string, unknown>,
		attachments?: Array<{
			fileName: string;
			fileSize: number;
			fileType: string;
			objectKey: string;
			url: string;
		}>
	) => {
		stopTyping();
		await handleSendMessage(richContent, attachments);
	};

	return (
		<div className="shrink-0">
			<TypingIndicator channelId={channelId} />
			<div className="p-4 pt-0">
				<MessageInput
					authorId={authStore.getState().user?.uid || ""}
					channelId={channelId}
					onCancelReply={handleCancelReply}
					onSendMessage={handleSendWithStopTyping}
					onTyping={startTyping}
					placeholder={`Message ${selectedChannel.name}`}
					replyingTo={replyingTo}
					roles={mentionRoles}
					users={mentionUsers}
				/>
				<MessageTour />
			</div>
		</div>
	);
}

export const ChannelView = {
	Root: ChannelViewRoot,
	Header: ChannelHeader,
	HeaderGroup: ChannelHeaderGroup,
	HeaderTitle: ChannelHeaderTitle,
	MessageList: ChannelMessageList,
	MessageInput: ChannelMessageInput,
	PeoplePopover: ChannelPeoplePopover,
};
